import React from 'react';
import { RangeModifier, LocaleUtils } from 'react-day-picker';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import 'react-day-picker/lib/style.css';
import { DateTime } from 'luxon';
import { dropdownIcon, Icon, IconSizes, nextIcon, previousIcon, Input, FlexGrid, Label, FlexGridNoGap } from '@ctek/design-system';
import { FormattedMessage } from 'react-intl';

import { DateRange } from 'century-core/core-apis/learn/aggregations/aggregations';
import { formatDate } from 'century-core/core-utils/utils/date/date';
import { parseDateFromFormat } from 'century-core/core-utils/utils/date/date';

function parseDate(str: string, format: string) {
  const parsed = parseDateFromFormat(str, format);
  return parsed && parsed.toJSDate();
}

interface Props {
  apply: (dateRange: DateRange, text: string) => void;
  initialSelection?: { start: string; end: string };
  fromYear: number;
}

interface FormProps {
  localeUtils: LocaleUtils;
  onChange: (date: Date) => void;
  date: Date;
  fromYear: number;
}

function YearMonthForm(props: FormProps) {
  const { fromYear, onChange, localeUtils, date } = props;

  const fromMonth = new Date(fromYear, 0);
  const toMonth = new Date();

  const months = localeUtils.getMonths();
  const years = [];
  for (let i = fromMonth.getFullYear(); i <= toMonth.getFullYear(); i += 1) {
    years.push(i);
  }

  const handleChange = function handleChange(e: any) {
    const { year, month } = e.target.form;
    // new Date expects 0 indexed months
    onChange(new Date(year.value, month.value - 1));
  };

  const month = date.getMonth() + 1; // Month is 0 indexed annoyingly so Jan=0, Feb=1 ...
  const monthName = months[date.getMonth()];

  return (
    <form className="rc-day-picker-input__caption">
      <div className="rc-day-picker-input__select">
        <div className="rc-day-picker-input__select-label">
          {monthName}
          <Icon icon={dropdownIcon} size={IconSizes.SMALL} />
        </div>
        <select name="month" onChange={handleChange} value={month} aria-label="month label">
          {months.map((month: string, i: number) => (
            <option key={month} value={i + 1}>
              {month}
            </option>
          ))}
        </select>
      </div>
      <div className="rc-day-picker-input__select">
        <div className="rc-day-picker-input__select-label">
          {date.getFullYear()}
          <Icon icon={dropdownIcon} size={IconSizes.SMALL} />
        </div>
        <select name="year" onChange={handleChange} value={date.getFullYear()} aria-label="year label">
          {years.map(year => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
    </form>
  );
}

const Navbar = ({ onPreviousClick, onNextClick, month }: any) => {
  return (
    <div className="rc-day-picker-input__navbar">
      <button className="rc-day-picker-input__prev" onClick={() => onPreviousClick()}>
        <Icon icon={previousIcon} size={IconSizes.SMALL} />
      </button>
      <button className="rc-day-picker-input__next" onClick={() => onNextClick()} disabled={DateTime.local().hasSame(month, 'month')}>
        <Icon icon={nextIcon} size={IconSizes.SMALL} />
      </button>
    </div>
  );
};

export const CustomDateRange: React.SFC<Props> = props => {
  const [dateRange, setDateRange] = React.useState<RangeModifier>(
    props.initialSelection
      ? {
          from: new Date(props.initialSelection.start),
          to: new Date(props.initialSelection.end),
        }
      : {
          from: new Date(),
          to: new Date(),
        }
  );
  const [toMonth, setToMonth] = React.useState<Date | undefined>(undefined);
  const [fromMonth, setFromMonth] = React.useState<Date | undefined>(undefined);
  const inputEl = React.useRef<HTMLInputElement>(null);

  const getCustomTimeSelectionText = (customDateRange: RangeModifier): string => {
    const dateFormat: string = 'd MMM';
    const comparisonDate = DateTime.fromJSDate(customDateRange.to as Date).minus({ days: 365 });
    let timeSelectionText = '';
    const startDate = DateTime.fromJSDate(customDateRange.from as Date);
    if (startDate <= comparisonDate) {
      timeSelectionText = `${DateTime.fromJSDate(customDateRange.from as Date).toFormat(dateFormat)} - ${DateTime.fromJSDate(
        customDateRange.to as Date
      ).toFormat(dateFormat)} `;
    } else {
      timeSelectionText = `${DateTime.fromJSDate(customDateRange.from as Date).toFormat(dateFormat)} - ${DateTime.fromJSDate(
        customDateRange.to as Date
      ).toFormat(dateFormat)} `;
    }
    return timeSelectionText;
  };
  React.useEffect(() => {
    const d: DateRange = {
      startDate: (dateRange.from as Date).toISOString(),
      endDate: (dateRange.to as Date).toISOString(),
    };
    const text = getCustomTimeSelectionText(dateRange);
    props.apply(d, text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);
  const handleFromChange = (from: Date) => {
    // Change the from date and focus the "to" input field
    const newDateRange: RangeModifier = {
      ...dateRange,
      from,
    };
    setDateRange(newDateRange);
  };
  const handleToChange = (to: Date) => {
    const newDateRange: RangeModifier = {
      ...dateRange,
      to,
    };
    setDateRange(newDateRange);
  };
  const handleYearMonthChange = (month: Date, context: 'to' | 'from') => {
    context === 'to' ? setToMonth(month) : setFromMonth(month);
  };
  const { from, to } = dateRange;
  const modifiers = { start: from as Date, end: to as Date };
  const FORMAT = 'dd MMM yyyy';

  return (
    <div className="rc-day-picker-input">
      <FlexGrid noGap={FlexGridNoGap.VERTICAL}>
        <FlexGrid.Col xs={6}>
          <Label>
            <Label.Text>
              <FormattedMessage id="custom-range-select-start" defaultMessage="From" />
            </Label.Text>
            <DayPickerInput
              value={from as Date}
              placeholder="From"
              formatDate={formatDate}
              format={FORMAT}
              parseDate={parseDate}
              dayPickerProps={{
                selectedDays: [from as Date, { from, to }],
                disabledDays: { after: to as Date },
                toMonth: to as Date,
                month: fromMonth,
                modifiers,
                numberOfMonths: 1,
                navbarElement: (props: any) => <Navbar {...props} />,
                onDayClick: () => inputEl?.current?.focus(),
                captionElement: ({ date, localeUtils }) => (
                  <YearMonthForm
                    fromYear={props.fromYear}
                    date={date}
                    localeUtils={localeUtils}
                    onChange={(m: Date) => handleYearMonthChange(m, 'from')}
                  />
                ),
              }}
              onDayChange={(day: Date) => handleFromChange(day)}
              component={(props: any) => <Input type="text" {...props} />}
            />
          </Label>
        </FlexGrid.Col>
        <FlexGrid.Col xs={6}>
          <Label>
            <Label.Text>
              <FormattedMessage id="custom-range-select-end" defaultMessage="To" />
            </Label.Text>
            <DayPickerInput
              ref={el => inputEl}
              value={to as Date}
              placeholder="To"
              formatDate={formatDate}
              format={FORMAT}
              parseDate={parseDate}
              dayPickerProps={{
                selectedDays: [from as Date, { from, to }],
                disabledDays: { before: from as Date },
                modifiers,
                month: toMonth,
                fromMonth: from as Date,
                numberOfMonths: 1,
                navbarElement: (props: any) => <Navbar {...props} />,
                captionElement: ({ date, localeUtils }) => (
                  <YearMonthForm
                    fromYear={props.fromYear}
                    date={date}
                    localeUtils={localeUtils}
                    onChange={(m: Date) => handleYearMonthChange(m, 'to')}
                  />
                ),
              }}
              onDayChange={(day: Date) => handleToChange(day)}
              component={(props: any) => <Input type="text" {...props} />}
            />
          </Label>
        </FlexGrid.Col>
      </FlexGrid>
    </div>
  );
};
